import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { PlayerData, KpiPerRole } from "@/api/player";
import { getPointsBadge, getKpiTier } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/shadcn/dialog";

interface Props {
    playerData: PlayerData | undefined;
    isLoading?: boolean;
}

type PositionKey = "LD" | "RD" | "LW" | "C" | "RW";

const ZONE_RECTS: Record<PositionKey, { x: number; y: number; w: number; h: number }> = {
    LW: { x: 5, y: 5, w: 96, h: 90 },
    C: { x: 101, y: 5, w: 98, h: 90 },
    RW: { x: 199, y: 5, w: 96, h: 90 },
    LD: { x: 5, y: 95, w: 145, h: 140 },
    RD: { x: 150, y: 95, w: 145, h: 140 },
};

const POSITION_DOTS: Record<PositionKey, { cx: number; cy: number }> = {
    LW: { cx: 53, cy: 52 },
    C: { cx: 150, cy: 52 },
    RW: { cx: 247, cy: 52 },
    LD: { cx: 77, cy: 165 },
    RD: { cx: 222, cy: 165 },
};

const HALF_ICE_PATH = "M 5,5 L 295,5 L 295,190 A 45,45 0 0,1 250,235 L 50,235 A 45,45 0 0,1 5,190 Z";
const VB = "0 0 300 240";

function fmt(v: number | null | undefined, decimals = 0, suffix = ""): string {
    if (v == null) return "—";
    return v.toFixed(decimals) + suffix;
}

function fmtPct(v: number | null | undefined): string {
    if (v == null) return "—";
    return (v * 100).toFixed(0) + "%";
}

interface CategoryBarProps {
    label: string;
    value: number | null | undefined;
    max: number;
    color: string;
}

function CategoryBar({ label, value, max, color }: CategoryBarProps) {
    if (value == null) return null;
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div className="flex items-center gap-2">
            <span
                className="text-[9px] uppercase tracking-widest font-bold w-10 shrink-0"
                style={{ color: "rgba(0,0,0,0.38)" }}
            >
                {label}
            </span>
            <div className="flex-1 h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.08)" }}>
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="text-[10px] font-bold w-7 text-right shrink-0" style={{ color }}>
                {value.toFixed(1)}
            </span>
        </div>
    );
}

interface StatPairProps {
    l1: string;
    v1: string;
    l2?: string;
    v2?: string;
}

function StatPair({ l1, v1, l2, v2 }: StatPairProps) {
    return (
        <div className="flex items-center gap-1 py-[3px]" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
            <span
                className="text-[9px] uppercase tracking-wide font-semibold shrink-0"
                style={{ color: "rgba(0,0,0,0.35)", width: 70 }}
            >
                {l1}
            </span>
            <span className="text-[11px] font-bold shrink-0" style={{ color: "rgba(0,0,0,0.72)", width: 40 }}>
                {v1}
            </span>
            {l2 !== undefined && (
                <>
                    <span className="flex-1" />
                    <span
                        className="text-[9px] uppercase tracking-wide font-semibold shrink-0 text-right"
                        style={{ color: "rgba(0,0,0,0.35)", width: 70 }}
                    >
                        {l2}
                    </span>
                    <span
                        className="text-[11px] font-bold shrink-0 text-right"
                        style={{ color: "rgba(0,0,0,0.72)", width: 36 }}
                    >
                        {v2}
                    </span>
                </>
            )}
        </div>
    );
}

interface RoleDetailDialogProps {
    pos: string;
    kpi: KpiPerRole | null;
    color: string;
    open: boolean;
    onClose: () => void;
}

interface RoleDetailDialogProps {
    pos: string;
    kpi: KpiPerRole | null;
    color: string;
    open: boolean;
    onClose: () => void;
    playerData: PlayerData | undefined;
}

function RoleDetailDialog({ pos, kpi, color, open, onClose }: RoleDetailDialogProps) {
    const tier = kpi?.avgKpi != null ? getKpiTier(kpi.avgKpi) : null;

    const hasCategories =
        kpi?.avgDefensivePoints != null ||
        kpi?.avgOffensivePoints != null ||
        kpi?.avgTeamPlayPoints != null ||
        kpi?.avgEffortPoints != null ||
        kpi?.avgBonusPoints != null;

    const hasStats = kpi?.corsiForPercentage != null || kpi?.goals != null || kpi?.topSpeed != null;

    const contests =
        (kpi?.puckContestParticipations ?? 0) > 0
            ? `${kpi?.puckContestsWon ?? 0}/${kpi?.puckContestParticipations ?? 0}`
            : "—";

    const faceoffs = (kpi?.faceoffCount ?? 0) > 0 ? `${kpi?.faceoffWins ?? 0}/${kpi?.faceoffCount ?? 0}` : "—";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-xs p-5">
                <div
                    className="rounded-lg px-4 py-3 mb-4"
                    style={{ background: "#e8eef8", borderLeft: `4px solid ${color}` }}
                >
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-black tracking-widest" style={{ color }}>
                                    {pos}
                                </span>
                                {tier && (
                                    <span
                                        className="text-[10px] uppercase tracking-wide font-semibold"
                                        style={{ color: "rgba(0,0,0,0.35)" }}
                                    >
                                        {tier.label}
                                    </span>
                                )}
                            </div>
                            {kpi?.matchesPlayed != null && (
                                <div className="flex gap-3 mt-1">
                                    <span className="text-[10px]" style={{ color: "rgba(0,0,0,0.4)" }}>
                                        {kpi.matchesPlayed} GP
                                    </span>
                                    <span className="text-[10px]" style={{ color: "rgba(0,0,0,0.4)" }}>
                                        {kpi.timeOnIce} min TOI
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-2xl font-black leading-none" style={{ color }}>
                                {kpi?.avgKpi != null ? kpi.avgKpi.toFixed(1) : "—"}
                            </div>
                            <div
                                className="text-[8px] uppercase tracking-widest mt-0.5"
                                style={{ color: "rgba(0,0,0,0.3)" }}
                            >
                                avg kpi
                            </div>
                        </div>
                    </div>
                    {kpi?.avgKpi != null && (
                        <div
                            className="mt-2 h-0.5 rounded-full overflow-hidden"
                            style={{ background: "rgba(0,0,0,0.08)" }}
                        >
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${kpi.avgKpi}%`, background: color }}
                            />
                        </div>
                    )}
                </div>

                {hasCategories && (
                    <div className="mb-4">
                        <p
                            className="text-[9px] uppercase tracking-widest font-bold mb-2"
                            style={{ color: "rgba(0,0,0,0.3)" }}
                        >
                            KPI Breakdown
                        </p>
                        <div className="flex flex-col gap-1.5">
                            <CategoryBar label="DEF" value={kpi?.avgDefensivePoints} max={20} color={color} />
                            <CategoryBar label="OFF" value={kpi?.avgOffensivePoints} max={20} color={color} />
                            <CategoryBar label="TEAM" value={kpi?.avgTeamPlayPoints} max={20} color={color} />
                            <CategoryBar label="EFFORT" value={kpi?.avgEffortPoints} max={20} color={color} />
                            <CategoryBar label="BONUS" value={kpi?.avgBonusPoints} max={10} color={color} />
                        </div>
                    </div>
                )}

                {hasStats && (
                    <div>
                        <p
                            className="text-[9px] uppercase tracking-widest font-bold mb-1.5"
                            style={{ color: "rgba(0,0,0,0.3)" }}
                        >
                            Stats
                        </p>
                        <div className="flex flex-col">
                            <StatPair
                                l1="Corsi%"
                                v1={fmtPct(kpi?.corsiForPercentage)}
                                l2="Fenwick%"
                                v2={fmtPct(kpi?.fenwickForPercentage)}
                            />
                            <StatPair l1="Goals" v1={fmt(kpi?.goals)} l2="Shots" v2={fmt(kpi?.shots)} />
                            <StatPair l1="+/-" v1={fmt(kpi?.plusMinusTotal)} l2="Blocked" v2={fmt(kpi?.blockedShots)} />
                            <StatPair
                                l1="Top Spd"
                                v1={fmt(kpi?.topSpeed, 1, " km/h")}
                                l2="Distance"
                                v2={fmt(kpi?.distanceTravelled, 0, " m")}
                            />
                            <StatPair l1="Contests" v1={contests} l2="Faceoffs" v2={faceoffs} />
                            <StatPair
                                l1="Press.Given"
                                v1={fmt(kpi?.averagePressureGiven, 2)}
                                l2="Press.Rec"
                                v2={fmt(kpi?.averagePressureReceived, 2)}
                            />
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function PlayerRinkPositionMap({ playerData, isLoading }: Props) {
    const [selectedPos, setSelectedPos] = useState<string | null>(null);

    const kpiPerRole = playerData?.kpiPerRole ?? [];
    const roleMap = new Map(kpiPerRole.map((k) => [k.role, k]));
    const playerRoles = new Set(playerData?.role ?? []);

    const overallKpi = roleMap.get("Overall") ?? null;

    const positionEntries = (["LD", "RD", "LW", "C", "RW"] as PositionKey[]).map((pos) => {
        const specificKpi = roleMap.get(pos) ?? null;
        const kpi = specificKpi ?? (playerRoles.has(pos) ? overallKpi : null);
        const isActive = playerRoles.has(pos) || !!specificKpi;
        const color = isActive && kpi?.avgKpi != null ? getKpiTier(kpi.avgKpi).color : "#4b5563";
        return { pos, kpi, isActive, color };
    });

    const activePositions = positionEntries.filter((e) => e.isActive);
    const selectedEntry = activePositions.find((e) => e.pos === selectedPos) ?? null;

    return (
        <>
            <div className="h-full rounded-xl overflow-hidden flex flex-col" style={{ background: "#050066" }}>
                <div className="flex-1 flex flex-col items-center justify-center px-4 py-4 gap-3">
                    <div className="w-full max-w-[260px] shrink-0">
                        <div
                            className="relative w-full overflow-hidden"
                            style={{ paddingBottom: `${(240 / 300) * 100}%` }}
                        >
                            <svg
                                className="absolute inset-0 w-full h-full"
                                viewBox={VB}
                                preserveAspectRatio="xMidYMid meet"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs>
                                    <clipPath id="pmRinkClip">
                                        <path d={HALF_ICE_PATH} />
                                    </clipPath>
                                </defs>

                                <path d={HALF_ICE_PATH} fill="#dbeafe" />

                                <g clipPath="url(#pmRinkClip)">
                                    {positionEntries.map(({ pos, isActive, color }) => {
                                        const { x, y, w, h } = ZONE_RECTS[pos];
                                        return (
                                            <rect
                                                key={`z-${pos}`}
                                                x={x}
                                                y={y}
                                                width={w}
                                                height={h}
                                                fill={color}
                                                fillOpacity={isActive ? 0.28 : 0.04}
                                                stroke={color}
                                                strokeWidth="1"
                                                strokeOpacity={isActive ? 0.55 : 0.06}
                                            />
                                        );
                                    })}
                                </g>

                                <line
                                    x1="5"
                                    x2="295"
                                    y1="5"
                                    y2="5"
                                    stroke="#c8102e"
                                    strokeWidth="3"
                                    strokeOpacity="0.9"
                                />
                                <line
                                    x1="5"
                                    x2="295"
                                    y1="95"
                                    y2="95"
                                    stroke="#0038A8"
                                    strokeWidth="3"
                                    strokeOpacity="0.9"
                                />

                                <circle cx="75" cy="72" r="3.5" fill="#c8102e" fillOpacity="0.8" />
                                <circle cx="225" cy="72" r="3.5" fill="#c8102e" fillOpacity="0.8" />

                                <circle
                                    cx="75"
                                    cy="160"
                                    r="25"
                                    fill="none"
                                    stroke="#c8102e"
                                    strokeWidth="1.5"
                                    strokeOpacity="0.7"
                                />
                                <circle
                                    cx="225"
                                    cy="160"
                                    r="25"
                                    fill="none"
                                    stroke="#c8102e"
                                    strokeWidth="1.5"
                                    strokeOpacity="0.7"
                                />
                                <circle cx="75" cy="160" r="3.5" fill="#c8102e" fillOpacity="0.8" />
                                <circle cx="225" cy="160" r="3.5" fill="#c8102e" fillOpacity="0.8" />

                                <line
                                    x1="30"
                                    x2="270"
                                    y1="190"
                                    y2="190"
                                    stroke="#c8102e"
                                    strokeWidth="1.5"
                                    strokeOpacity="0.7"
                                />
                                <path
                                    d="M 125,190 L 125,168 A 25,25 0 0,1 175,168 L 175,190 Z"
                                    fill="#bfdbfe"
                                    fillOpacity="0.6"
                                    stroke="#c8102e"
                                    strokeWidth="1"
                                />
                                <rect
                                    x="125"
                                    y="217"
                                    width="50"
                                    height="18"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    opacity="0.6"
                                />

                                <path
                                    d="M 5,5 L 5,190 A 45,45 0 0,0 50,235 L 250,235 A 45,45 0 0,0 295,190 L 295,5"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="3"
                                />

                                {!isLoading &&
                                    positionEntries.map(({ pos, isActive, color, kpi }) => {
                                        const { cx, cy } = POSITION_DOTS[pos];
                                        return (
                                            <g key={`d-${pos}`}>
                                                {isActive && (
                                                    <circle cx={cx} cy={cy} r={22} fill={color} fillOpacity={0.15} />
                                                )}
                                                <circle
                                                    cx={cx}
                                                    cy={cy}
                                                    r={isActive ? 13 : 7}
                                                    fill={isActive ? color : "#1e3a5f"}
                                                    fillOpacity={isActive ? 0.9 : 0.3}
                                                    stroke={isActive ? color : "#374151"}
                                                    strokeWidth={isActive ? 2 : 1}
                                                    strokeOpacity={isActive ? 1 : 0.2}
                                                />
                                                <text
                                                    x={cx}
                                                    y={cy - 19}
                                                    textAnchor="middle"
                                                    fill={isActive ? color : "#6b7280"}
                                                    fillOpacity={isActive ? 1 : 0.3}
                                                    fontSize="13"
                                                    fontWeight="bold"
                                                    fontFamily="system-ui, sans-serif"
                                                >
                                                    {pos}
                                                </text>
                                                {isActive && kpi?.avgKpi != null && (
                                                    <text
                                                        x={cx}
                                                        y={cy + 30}
                                                        textAnchor="middle"
                                                        fill={color}
                                                        fontSize="11"
                                                        fontFamily="system-ui, sans-serif"
                                                    >
                                                        {kpi.avgKpi.toFixed(1)}
                                                    </text>
                                                )}
                                            </g>
                                        );
                                    })}
                            </svg>
                        </div>
                    </div>

                    <div className="w-full flex flex-col gap-2">
                        {activePositions.length > 0 ? (
                            activePositions.map(({ pos, kpi, color }) => {
                                const tier = kpi?.avgKpi != null ? getKpiTier(kpi.avgKpi) : null;
                                return (
                                    <button
                                        key={pos}
                                        className="rounded-lg px-3 py-2 text-left w-full cursor-pointer transition-all hover:brightness-95 active:brightness-90 group"
                                        style={{
                                            background: "#f0f4fc",
                                            borderLeft: `3px solid ${color}`,
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                                        }}
                                        onClick={() => setSelectedPos(pos)}
                                    >
                                        <div className="flex items-start justify-between gap-1">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span
                                                        className="text-xs font-black tracking-widest"
                                                        style={{ color }}
                                                    >
                                                        {pos}
                                                    </span>
                                                    {tier && (
                                                        <span
                                                            className="text-[9px] uppercase tracking-wide font-semibold truncate"
                                                            style={{ color: "rgba(0,0,0,0.35)" }}
                                                        >
                                                            {tier.label}
                                                        </span>
                                                    )}
                                                </div>
                                                {kpi?.matchesPlayed != null && (
                                                    <div className="flex gap-2 mt-0.5">
                                                        <span
                                                            className="text-[9px]"
                                                            style={{ color: "rgba(0,0,0,0.4)" }}
                                                        >
                                                            {kpi.matchesPlayed} GP
                                                        </span>
                                                        <span
                                                            className="text-[9px]"
                                                            style={{ color: "rgba(0,0,0,0.4)" }}
                                                        >
                                                            {kpi.timeOnIce} min
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-0.5 shrink-0">
                                                {kpi?.avgKpi != null ? (
                                                    (() => {
                                                        const b = getPointsBadge(kpi.avgKpi);
                                                        return (
                                                            <div
                                                                className={`px-1.5 py-0.5 rounded border ${b.bg} ${b.text} ${b.border} text-sm font-headline font-black leading-none`}
                                                            >
                                                                {kpi.avgKpi.toFixed(1)}
                                                            </div>
                                                        );
                                                    })()
                                                ) : (
                                                    <span
                                                        className="text-sm font-black"
                                                        style={{ color: "rgba(0,0,0,0.4)" }}
                                                    >
                                                        —
                                                    </span>
                                                )}
                                                <ChevronRight
                                                    className="size-3 transition-transform group-hover:translate-x-0.5"
                                                    style={{ color: "rgba(0,0,0,0.4)" }}
                                                />
                                            </div>
                                        </div>
                                        {kpi?.avgKpi != null && (
                                            <div
                                                className="mt-2 h-0.5 rounded-full overflow-hidden"
                                                style={{ background: "rgba(0,0,0,0.08)" }}
                                            >
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{ width: `${kpi.avgKpi}%`, background: color }}
                                                />
                                            </div>
                                        )}
                                    </button>
                                );
                            })
                        ) : (
                            <span className="text-[11px] italic" style={{ color: "rgba(255,255,255,0.25)" }}>
                                No position data
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {selectedEntry && selectedPos !== null && (
                <RoleDetailDialog
                    pos={selectedEntry.pos}
                    kpi={selectedEntry.kpi}
                    color={selectedEntry.color}
                    open={selectedPos !== null}
                    onClose={() => setSelectedPos(null)}
                    playerData={playerData}
                />
            )}
        </>
    );
}
